import { ApplicationView, useApplicationView } from "@hooks/useApplicationView";
import { useCurrentUserIsWorkspaceOrganizationMember } from "@hooks/useCurrentUserIsWorkspaceOrganizationMember";
import { useScope } from "@state/scopeContext.lib";

/**
 * Returns whether the current user is in an accountant/firm context, using
 * the same rule as useHelpCenterUrl:
 *   - User is browsing the firm/organization dashboard (ORGANIZATION_VIEW), OR
 *   - User is a firm member viewing a client workspace (isOrganizationMember)
 *
 * `isReady` is false while the workspace membership check is still loading,
 * so callers can avoid flashing the wrong UI before we know the persona.
 */
export function useIsAccountantSupportContext(): {
    isAccountant: boolean;
    isReady: boolean;
} {
    const applicationView = useApplicationView();
    const { workspaceId } = useScope();
    const { isOrganizationMember, isFetched } =
        useCurrentUserIsWorkspaceOrganizationMember();

    if (applicationView === ApplicationView.ORGANIZATION_VIEW) {
        return { isAccountant: true, isReady: true };
    }

    const needsWorkspaceMembershipCheck =
        applicationView === ApplicationView.MAIN && workspaceId;

    if (needsWorkspaceMembershipCheck && !isFetched) {
        return { isAccountant: false, isReady: false };
    }

    return { isAccountant: isOrganizationMember, isReady: true };
}
